import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getMalnutritionRiskAssessment,
  getPatientWeights,
  postMalnutritionFeedback,
} from "./endpoints";
import { malnutritionUseMocks } from "./mocks";

import type { UseMutationResult, UseQueryResult } from "react-query";
import type {
  MalnutritionFeedbackVariables,
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";

// Both endpoints serve EBA-pushed data that changes with no user action to
// invalidate on, so poll while the embed is open (foreground only —
// refetchIntervalInBackground stays off globally).
const MALNUTRITION_REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export const useMalnutritionRiskQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<MalnutritionRiskAssessment, Error> => {
  // The fetcher returns mock vs real data based on the ?mock= param, so the mode
  // is part of the cache identity — otherwise toggling ?mock= without a full
  // reload could serve cached mock data in real mode (or vice versa).
  const useMocks = malnutritionUseMocks();
  return useQuery<MalnutritionRiskAssessment, Error>(
    ["malnutritionrisk", emrPatientID, emrHospitalID, useMocks],
    () => getMalnutritionRiskAssessment(emrPatientID, emrHospitalID),
    {
      enabled: !!emrPatientID && !!emrHospitalID,
      refetchInterval: MALNUTRITION_REFETCH_INTERVAL_MS,
    }
  );
};

export const usePatientWeightsQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<PatientWeightHistory, Error> => {
  const useMocks = malnutritionUseMocks();
  return useQuery<PatientWeightHistory, Error>(
    ["patientweights", emrPatientID, emrHospitalID, useMocks],
    () => getPatientWeights(emrPatientID, emrHospitalID),
    {
      enabled: !!emrPatientID && !!emrHospitalID,
      refetchInterval: MALNUTRITION_REFETCH_INTERVAL_MS,
    }
  );
};

// Records one step of the answer — the Yes/No choice or the follow-up. On
// success the risk-assessment query is invalidated so the card re-reads what
// was stored; the key prefix match ignores the trailing useMocks segment. The
// save settles only once that re-read lands, so a panel reopened straight
// after it seeds from the saved answer rather than the one before, and the
// card's pending vote holds until the server's answer replaces it.
//
// The EMR ids are mutation *variables* rather than hook arguments: the panel
// snapshots them when it opens, so a write in flight stays attributed to the
// patient it was dispatched for even if the user switches patients first.
export const useMalnutritionFeedbackMutation = (): UseMutationResult<
  void,
  Error,
  MalnutritionFeedbackVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, MalnutritionFeedbackVariables>(
    ({ emrPatientID, emrHospitalID, payload }) =>
      postMalnutritionFeedback(emrPatientID, emrHospitalID, payload),
    {
      onSuccess: (_data, { emrPatientID, emrHospitalID }) =>
        queryClient.invalidateQueries([
          "malnutritionrisk",
          emrPatientID,
          emrHospitalID,
        ]),
    }
  );
};
